import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { DeviceInfo, UserInfo, FarmInfo, DeviceStatus } from '@grosync/common';
import { fetchDevices } from '../../api/devices';
import { fetchUsers } from '../../api/users';
import { fetchFarms } from '../../api/farms';
import {
  AdminTopBar,
  ViewScroll,
  Panel,
  StatCard,
  StatusPill,
  Avatar,
  Button,
  Sym,
  type DeviceStatus as UiStatus,
} from '../../ui';
import { ContentToolbar } from '../../ui/ContentToolbar';
import { LoadingSpinner, PageError } from '../../components/LoadingSpinner';
import { DonutStatus, LegendRow } from './components/DonutStatus';
import { ActivityRow, type ActivityItem } from './components/ActivityRow';

/* ============================================================================
 * 통합 모니터링 — admin integrated monitoring dashboard.
 * Recreates admin-dashboard.jsx (Claude Design prototype) with real backend
 * data via TanStack Query.
 * ========================================================================= */

/** Map backend DeviceStatus → design UI status (ok/warn/off). */
function toUiStatus(s: DeviceStatus): UiStatus {
  switch (s) {
    case 'ACTIVE':
      return 'ok';
    case 'WARNING':
    case 'ERROR':
      return 'warn';
    case 'OFFLINE':
    default:
      return 'off';
  }
}

interface FarmRow {
  farm: FarmInfo;
  status: UiStatus;
  ownerName: string;
  region: string;
  devices: number;
  warns: number;
}

/** Derive a farm's UI status + warning count from its devices. */
function deriveFarmRows(
  farms: FarmInfo[],
  devices: DeviceInfo[],
  users: UserInfo[],
): FarmRow[] {
  const userById = new Map(users.map((u) => [u.id, u]));
  return farms.map((farm) => {
    const farmDevices = devices.filter((d) => d.farmId === farm.id);
    const uiStatuses = farmDevices.map((d) => toUiStatus(d.status));
    const warns = uiStatuses.filter((s) => s === 'warn').length;
    let status: UiStatus;
    if (warns > 0) {
      status = 'warn';
    } else if (farmDevices.length === 0 || uiStatuses.every((s) => s === 'off')) {
      status = 'off';
    } else {
      status = 'ok';
    }
    return {
      farm,
      status,
      ownerName: userById.get(farm.ownerId)?.name ?? '미지정',
      region: farm.location?.description ?? '위치 미등록',
      devices: farm.deviceCount ?? farmDevices.length,
      warns,
    };
  });
}

const ghostBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 32,
  padding: '0 6px 0 12px',
  borderRadius: 9999,
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  font: '600 13px/1 var(--gs-font-kr)',
  color: 'var(--md-sys-color-primary)',
};

function fmtNum(n: number): string {
  return n.toLocaleString('ko-KR');
}

// MOCK: 실시간 활동 이벤트 — 백엔드 활동 피드 API가 없어 샘플 데이터로 표시.
// 추후 fetchRecentAlerts() 등으로 교체 가능.
const MOCK_ACTIVITY: ActivityItem[] = [
  { icon: 'warning', sev: 'warn', who: '대성 토마토 농장', text: '온도 임계값 초과 경고', t: '방금 전' },
  { icon: 'person_add', sev: 'info', who: '신규 회원', text: '가입을 신청했습니다', t: '12분 전' },
  { icon: 'sensors', sev: 'sys', who: '장비 GS-1042', text: '펌웨어 업데이트 완료', t: '1시간 전' },
  { icon: 'wifi_off', sev: 'warn', who: '한울 딸기 농장', text: '장비 오프라인 전환', t: '3시간 전' },
];

export default function DashboardView(): React.ReactElement {
  const navigate = useNavigate();

  const devicesQuery = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    refetchInterval: 30_000,
  });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const farmsQuery = useQuery({ queryKey: ['farms'], queryFn: fetchFarms });

  const isLoading = devicesQuery.isLoading || usersQuery.isLoading || farmsQuery.isLoading;
  const error = devicesQuery.error ?? usersQuery.error ?? farmsQuery.error;

  const refetchAll = (): void => {
    void devicesQuery.refetch();
    void usersQuery.refetch();
    void farmsQuery.refetch();
  };

  const now = new Date();
  const stamp = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(
    now.getDate(),
  ).padStart(2, '0')} 기준`;

  const refreshAction = (
    <Button
      variant="outlined"
      icon="refresh"
      onClick={refetchAll}
      style={{ height: 42, borderRadius: 9999 }}
    >
      새로고침
    </Button>
  );

  if (isLoading) {
    return (
      <>
        <AdminTopBar title="통합 모니터링" subtitle={`전국 농장 · 회원 · 장비 실시간 현황 · ${stamp}`} />
        <ViewScroll>
          <LoadingSpinner />
        </ViewScroll>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminTopBar
          title="통합 모니터링"
          subtitle={`전국 농장 · 회원 · 장비 실시간 현황 · ${stamp}`}
        />
        <ViewScroll>
          <ContentToolbar actions={refreshAction} />
          <PageError message={(error as Error).message} />
        </ViewScroll>
      </>
    );
  }

  const devices: DeviceInfo[] = devicesQuery.data ?? [];
  const users: UserInfo[] = usersQuery.data ?? [];
  const farms: FarmInfo[] = farmsQuery.data ?? [];

  const farmRows = deriveFarmRows(farms, devices, users);

  // ---- derived counts ----
  const members = users.length;
  const activeMembers = users.filter((u) => u.isActive).length;
  // MOCK: 백엔드에 "가입 승인 대기" 개념이 없음 — 비활성 사용자를 대기 목록으로 표시.
  const pendingMembers = users.filter((u) => !u.isActive);
  const pending = pendingMembers.length;

  const okFarms = farmRows.filter((f) => f.status === 'ok').length;
  const warnFarmsCount = farmRows.filter((f) => f.status === 'warn').length;
  const offFarms = farmRows.filter((f) => f.status === 'off').length;
  const totalFarms = farmRows.length;

  const totalDevices = devices.length;
  const totalWarns = devices.filter((d) => toUiStatus(d.status) === 'warn').length;

  const okRate = totalFarms > 0 ? Math.round((okFarms / totalFarms) * 100) : 0;

  const warnFarms = farmRows
    .filter((f) => f.status === 'warn' || f.status === 'off')
    .sort((a, b) => b.warns - a.warns || (a.status === 'off' ? 1 : -1));

  return (
    <>
      <AdminTopBar
        title="통합 모니터링"
        subtitle={`전국 농장 · 회원 · 장비 실시간 현황 · ${stamp}`}
      />
      <ViewScroll gap={18}>
        <ContentToolbar actions={refreshAction} />
        {/* KPI row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <StatCard
            icon="group"
            label="전체 회원"
            value={fmtNum(members)}
            unit="명"
            tone="secondary"
            sub={`활성 ${activeMembers} · 승인대기 ${pending}`}
          />
          <StatCard
            icon="agriculture"
            label="등록 농장"
            value={fmtNum(totalFarms)}
            unit="곳"
            tone="primary"
            sub={`정상 ${okFarms} · 경고 ${warnFarmsCount} · 오프 ${offFarms}`}
          />
          <StatCard
            icon="sensors"
            label="연결 장비"
            value={fmtNum(totalDevices)}
            unit="대"
            tone="primary"
            sub={`정상 ${devices.filter((d) => toUiStatus(d.status) === 'ok').length}대 가동 중`}
          />
          <StatCard
            icon="warning"
            label="미해결 경고"
            value={fmtNum(totalWarns)}
            unit="건"
            tone="warn"
            sub={`경고 농장 ${warnFarmsCount}곳에서 발생`}
          />
        </div>

        {/* main grid */}
        <div
          style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 18, alignItems: 'start' }}
        >
          {/* LEFT column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* farm status overview */}
            <Panel title="농장 상태 현황" subtitle="전국 농장 실시간 상태 분포">
              <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
                <DonutStatus ok={okFarms} warn={warnFarmsCount} off={offFarms} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <LegendRow
                    color="var(--gs-primary-50)"
                    label="정상 운영"
                    value={okFarms}
                    total={totalFarms}
                  />
                  <LegendRow
                    color="var(--gs-warning)"
                    label="경고 발생"
                    value={warnFarmsCount}
                    total={totalFarms}
                  />
                  <LegendRow color="#B8BEB2" label="오프라인" value={offFarms} total={totalFarms} />
                  <div
                    style={{
                      height: 1,
                      background: 'var(--md-sys-color-outline-variant)',
                      margin: '2px 0',
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Sym
                      name="check_circle"
                      size={16}
                      fill={1}
                      style={{ color: 'var(--gs-primary-50)' }}
                    />
                    <span
                      style={{
                        font: '500 13px/18px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                      }}
                    >
                      가동률{' '}
                      <b style={{ color: 'var(--md-sys-color-on-surface)' }}>{okRate}%</b> · 정상 범위
                    </span>
                  </div>
                </div>
              </div>
            </Panel>

            {/* warning farms */}
            <Panel
              title="주의가 필요한 농장"
              subtitle={`경고·오프라인 ${warnFarms.length}곳`}
              action={
                <button onClick={() => navigate('/map')} style={ghostBtn}>
                  지도에서 보기
                  <Sym name="arrow_forward" size={16} />
                </button>
              }
              pad={false}
            >
              {warnFarms.length === 0 ? (
                <div
                  style={{
                    padding: '32px 20px',
                    textAlign: 'center',
                    font: '400 13px/18px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                  }}
                >
                  주의가 필요한 농장이 없습니다.
                </div>
              ) : (
                <div>
                  {warnFarms.map((f, i) => (
                    <div
                      key={f.farm.id}
                      className="gs-row"
                      onClick={() => navigate('/map')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '13px 20px',
                        cursor: 'pointer',
                        borderBottom:
                          i === warnFarms.length - 1
                            ? 0
                            : '1px solid var(--md-sys-color-outline-variant)',
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 11,
                          flexShrink: 0,
                          background:
                            f.status === 'warn'
                              ? 'var(--gs-warning-container)'
                              : 'var(--md-sys-color-surface-container-high)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Sym
                          name={f.status === 'warn' ? 'priority_high' : 'wifi_off'}
                          size={19}
                          fill={1}
                          style={{ color: f.status === 'warn' ? 'var(--gs-warning)' : '#9AA095' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            font: '600 14px/18px var(--gs-font-kr)',
                            color: 'var(--md-sys-color-on-surface)',
                          }}
                        >
                          {f.farm.name}
                        </div>
                        <div
                          style={{
                            font: '400 12px/16px var(--gs-font-kr)',
                            color: 'var(--md-sys-color-on-surface-variant)',
                            marginTop: 2,
                          }}
                        >
                          {f.region} · {f.ownerName} · 장비 {f.devices}대
                        </div>
                      </div>
                      {f.status === 'warn' ? (
                        <span className="gs-tag gs-tag-warn">
                          <Sym name="warning" size={12} fill={1} />
                          경고 {f.warns}
                        </span>
                      ) : (
                        <StatusPill s="off" />
                      )}
                      <Sym
                        name="chevron_right"
                        size={20}
                        style={{ color: 'var(--md-sys-color-on-surface-variant)' }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>

          {/* RIGHT column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* pending approvals */}
            <Panel
              title="가입 승인 대기"
              subtitle={
                pending > 0
                  ? `${pending}명이 승인을 기다리고 있어요`
                  : '대기 중인 회원이 없습니다'
              }
              action={
                <button onClick={() => navigate('/members')} style={ghostBtn}>
                  회원관리
                  <Sym name="arrow_forward" size={16} />
                </button>
              }
            >
              {pending === 0 ? (
                <div
                  style={{
                    padding: '20px 0',
                    textAlign: 'center',
                    font: '400 13px/18px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                  }}
                >
                  {/* MOCK: 백엔드에 승인 대기 개념이 없어 비활성 회원을 표시합니다. */}
                  승인 대기 중인 회원이 없습니다.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {pendingMembers.map((m) => (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px',
                        borderRadius: 14,
                        background: 'var(--md-sys-color-surface-container-low)',
                      }}
                    >
                      <Avatar name={m.name} size={38} color="var(--gs-warning-container)" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            font: '600 14px/18px var(--gs-font-kr)',
                            color: 'var(--md-sys-color-on-surface)',
                          }}
                        >
                          {m.name}
                        </div>
                        <div
                          style={{
                            font: '400 11px/14px var(--gs-font-kr)',
                            color: 'var(--md-sys-color-on-surface-variant)',
                            marginTop: 2,
                          }}
                        >
                          @{m.username} · 비활성 계정
                        </div>
                      </div>
                      <Button
                        variant="filled"
                        onClick={() => navigate('/members')}
                        style={{ height: 32, padding: '0 16px', font: '600 13px/1 var(--gs-font-kr)' }}
                      >
                        검토
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* live activity */}
            <Panel title="실시간 활동" subtitle="경고 · 회원 · 장비 이벤트">
              <div>
                {MOCK_ACTIVITY.map((a, i) => (
                  <ActivityRow key={i} a={a} last={i === MOCK_ACTIVITY.length - 1} />
                ))}
              </div>
            </Panel>
          </div>
        </div>
      </ViewScroll>
    </>
  );
}
